import { DEFAULT_DEBOUNCE_MS } from "$utils/consts";

export interface Cancelable {
  clear(): void;
}

// Corresponds to 10 frames at 60 Hz.
// A few bytes payload overhead when lodash/debounce is ~3 kB and debounce ~300 B.
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait?: number
): T & Cancelable {
  let timeout: string | number | NodeJS.Timeout | undefined;
  const timeoutMillis = wait || wait === 0 ? wait : DEFAULT_DEBOUNCE_MS;

  function debounced(...args: any[]) {
    const later = () => {
      // @ts-ignore
      func.apply(this, args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, timeoutMillis);
  }

  debounced.clear = () => {
    clearTimeout(timeout);
  };
  // @ts-ignore
  return debounced;
}

export interface Deferred<T> {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason: unknown) => void;
  promise: Promise<T>;
}

/** A polyfill for a shorthand way of using a Promise inline.
 *
 * It let's you more easily create a promise as a signal and resolve it later.
 *
 * Part of an upcoming change:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/withResolvers
 *
 * Idiom in many codebases: https://github.com/tc39/proposal-promise-with-resolvers?tab=readme-ov-file#existing-implementations
 *
 * Equivalent to:
 *
 * ```js
 * let resolve, reject;
 * const promise = new Promise((res, rej) => {
 *    resolve = res;
 *    reject = rej;
 * });
 * ```
 */
export function withResolvers<T = void>(): Deferred<T> {
  const out = {} as Deferred<T>;
  out.promise = new Promise((resolve_, reject_) => {
    out.resolve = resolve_;
    out.reject = reject_;
  });
  return out;
}
